import { usePostHog } from '@posthog/react';
import { useId } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { FOCUS_RING_INSET } from 'layout/NewDesignLayout/focusRing';
import { cn } from 'utils/cn';
import AiChatCapabilities, { AiChatCapabilitiesLink, useCapabilitiesView } from '../components/AiChatCapabilities';
import AiChatResumeCard from '../components/AiChatResumeCard';
import { AiChatGreeting, PromptSelectHandler } from '../components/AiChatSamplePrompts';
import PromptBadges from '../components/PromptBadges';
import {
    getPromptBadges,
    getPromptDisplayText,
    getPromptFill,
    getPromptId,
    getPromptLabelKey,
    getPromptTile,
} from './pagePrompts';
import { usePageContext } from './usePageContext';

interface FloatingAiEmptyStateProps {
    /** Suggested-prompt i18n keys for the current page, from `getPromptKeys`. */
    promptKeys: string[];
    onPromptSelect: PromptSelectHandler;
    /** Opens a previous conversation, for the "continue previous chat" card. */
    onSelectThread: (threadId: number) => void;
}

// "Case" reads "case" in a sentence; an initialism such as "BD effort" keeps its capitals.
const inSentence = (noun: string): string =>
    /^\p{Lu}\p{Ll}/u.test(noun) ? noun[0].toLowerCase() + noun.slice(1) : noun;

/** Intro shown in the floating panel until the conversation starts: greeting + page prompts, or What it can do. */
const FloatingAiEmptyState: React.FC<FloatingAiEmptyStateProps> = ({ promptKeys, onPromptSelect, onSelectThread }) => {
    const intl = useIntl();
    const posthog = usePostHog();
    const pageContext = usePageContext();
    const { view, show, leaveClassName } = useCapabilitiesView();
    const listId = useId();

    if (view === 'capabilities') {
        return <AiChatCapabilities onBack={() => show('returned')} className={leaveClassName} />;
    }

    // On a record's page the list says which record type it suits; on a list or the dashboard, the page.
    const suggestedLabel =
        pageContext?.id != null ? (
            <FormattedMessage
                id="ai-chat.floating.suggested-record"
                values={{
                    type: inSentence(
                        intl.formatMessage({ id: `ai-chat.floating.page-type.${pageContext.objectType}` })
                    ),
                }}
            />
        ) : (
            <FormattedMessage id="ai-chat.floating.suggested" />
        );

    return (
        <div className={cn('flex flex-col gap-5', leaveClassName)}>
            <AiChatGreeting size="compact" greetingId="ai-chat.hero.title" />
            <AiChatResumeCard surface="floating" onSelectThread={onSelectThread} />
            <section aria-labelledby={listId} className="flex flex-col gap-1.5">
                <div className="flex items-baseline justify-between gap-2">
                    <h3 id={listId} className="text-label text-muted-foreground uppercase">
                        {suggestedLabel}
                    </h3>
                    <AiChatCapabilitiesLink onClick={() => show('capabilities')} autoFocus={view === 'returned'} />
                </div>
                <ul className="-mx-2 flex flex-col">
                    {promptKeys.map((key) => {
                        const prompt = intl.formatMessage({ id: key });
                        const labelKey = getPromptLabelKey(key);
                        const label = intl.messages[labelKey] ? intl.formatMessage({ id: labelKey }) : prompt;
                        // Prompts carrying a placeholder open its picker — a chip list, or the case
                        // autocomplete; the rest just land in the composer, unsent, so they can be amended
                        // first. The row shows the short label with its starting value spelled out, while
                        // the full prompt, placeholder and all, goes to the handler.
                        const fill = getPromptFill(key);
                        const promptId = getPromptId(key);
                        const { icon: Icon, tileClass } = getPromptTile(key);
                        return (
                            <li key={key}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        posthog.capture('ai_sample_prompt_selected', {
                                            prompt_id: promptId,
                                            surface: 'floating',
                                        });
                                        onPromptSelect(prompt, fill?.optionsMap, fill?.config, promptId);
                                    }}
                                    className={cn(
                                        'hover:bg-row-stripe flex w-full cursor-pointer items-center gap-2.5 rounded-[12px] px-2 py-1 text-start transition-colors',
                                        FOCUS_RING_INSET
                                    )}
                                >
                                    <span
                                        className={cn(
                                            'grid size-6 shrink-0 place-items-center rounded-[7px]',
                                            tileClass
                                        )}
                                    >
                                        <Icon className="size-3.5" />
                                    </span>
                                    <span className="text-meta text-foreground min-w-0 flex-1 font-medium text-pretty">
                                        {getPromptDisplayText(key, label)}
                                    </span>
                                    <PromptBadges badges={getPromptBadges(key)} />
                                </button>
                            </li>
                        );
                    })}
                </ul>
            </section>
        </div>
    );
};

export default FloatingAiEmptyState;
